import React, { useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { Collapse, Spin } from 'antd';
import { DoubleRightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  apiV2AirCompressorOverviewPowerPost,
  V2AirCompressorOverviewPowerPostResponse,
  apiV2AirCompressorOverviewGetStrategyListPost,
  apiV2AirCompressorOverviewCountPost,
  V2AirCompressorOverviewGetStrategyListPostResponse,
  V2AirCompressorOverviewCountPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import runSafeIcon from '../../../../../imgs/run-safe@2x.png';
import circleIcon from '../../../../../imgs/icon-circle.png';
import runningIcon from '../../../../../imgs/status_running.png';
import stoppedIcon from '../../../../../imgs/status_stopped.png';
import standbyIcon from '../../../../../imgs/status_standby.png';
import strategyIcon from '../../../../../imgs/icon_strategy.png';
import styles from './index.module.scss';

enum StrategyTab {
  demand = 'demand',
  pressure = 'pressure',
}

const StrategyTabLabels: Record<StrategyTab, string> = {
  [StrategyTab.demand]: '需量控制策略',
  [StrategyTab.pressure]: '供气压力保障策略',
};

const StatusMap = {
  runCount: {
    label: '运行',
    icon: runningIcon,
  },
  waitCount: {
    label: '待机',
    icon: standbyIcon,
  },
  stopCount: {
    label: '停机',
    icon: stoppedIcon,
  },
};

const formatSummaryValue = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '--';
  }
  return value.toLocaleString('zh-CN', { maximumFractionDigits: 2 });
};

const formatValue = (value?: number, unit = 'kW') => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  return `${value}${unit}`;
};

interface ChangeInfo {
  circuit: string;
  changeType: number;
  targetPower: number;
  demandThreshold: number;
}

export type SummaryCardData = NonNullable<V2AirCompressorOverviewPowerPostResponse['list']>[number];
export type DemandControlStrategiesData = NonNullable<
  V2AirCompressorOverviewGetStrategyListPostResponse['demandControlStrategies']
>[number];

const CompressorStrategy: React.FC = () => {
  const [activeTab, setActiveTab] = useState<StrategyTab>(StrategyTab.demand);

  const { data: powerData, loading: powerLoading } = useRequest(apiV2AirCompressorOverviewPowerPost);
  const { data: statusCount, loading: statusLoading } = useRequest(apiV2AirCompressorOverviewCountPost);
  const { data: strategyData, loading: strategyLoading } = useRequest(() =>
    apiV2AirCompressorOverviewGetStrategyListPost({ time: dayjs().startOf('day').valueOf() })
  );

  const fixedCircuitNames = ['淼船线', '东船线', '洲船线'];

  const circuitList = useMemo(() => {
    const cards = powerData?.list?.slice(0, 3) ?? [];

    while (cards.length < 3) {
      cards.push({} as SummaryCardData);
    }
    return cards;
  }, [powerData]);

  const compressorTotal = useMemo(() => {
    const counts = [statusCount?.runCount, statusCount?.waitCount, statusCount?.stopCount];
    if (counts.every(count => typeof count !== 'number')) {
      return undefined;
    }
    return counts.reduce((total, count) => total + (typeof count === 'number' ? count : 0), 0);
  }, [statusCount]);

  const demandStrategies = useMemo(() => strategyData?.demandControlStrategies ?? [], [strategyData]);
  const pressureStrategies = useMemo(() => strategyData?.supplyPressureGuaranteeStrategies ?? [], [strategyData]);

  const isLoading = powerLoading || statusLoading || strategyLoading;

  const renderEmptyState = (text: string) => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIconWrap}>
        <img src={circleIcon} alt="" className={styles.emptyCircleIcon} />
        <img src={runSafeIcon} alt="" className={styles.emptySafetyIcon} />
      </div>
      <p className={styles.emptyText}>{text}</p>
    </div>
  );

  const handleDetailClick = (circuitId?: number) => {
    if (!circuitId) {
      return;
    }
    const query = new URLSearchParams({
      id: String(circuitId),
      dataType: '5',
      timeResolution: '1M',
    });
    window.open(`${window.DMEPLATFORMSORIGIN}/dmes/statistics/circuit?${query.toString()}`, '_blank');
  };

  const mapPowerContent = (data: DemandControlStrategiesData) => {
    const closeList = data.suggestClose ?? [];
    const openList = data.suggestOpen ?? [];
    const changes = (data.currentChange ?? []) as ChangeInfo[];

    return (
      <div className={styles.accordionBody}>
        {/* 建议 */}
        {(closeList.length > 0 || openList.length > 0) && (
          <div className={styles.accordionBlock}>
            <span className={styles.accordionBadge}>建议</span>
            <div className={styles.accordionContent}>
              {closeList.length > 0 && (
                <>
                  关闭
                  <span style={{ color: '#FF4D4F' }}>{closeList.join('、')}</span>
                  {openList.length > 0 ? '，' : ''}
                </>
              )}
              {openList.length > 0 && (
                <>
                  开启
                  <span style={{ color: '#16DD8E' }}>{openList.join('、')}</span>;
                </>
              )}
            </div>
          </div>
        )}
        {/* 执行后 */}
        {changes.length > 0 && (
          <div className={styles.accordionBlock}>
            <span className={styles.accordionBadge}>执行后</span>
            <div className={styles.accordionContent}>
              {changes.map((c, i) => (
                <span key={i}>
                  {c.circuit}功率
                  {c.changeType === 1 ? '上升至' : '降低至'}
                  <span style={{ color: '#52E7FF' }}>{formatValue(c.targetPower)}</span>（控制阈值:{' '}
                  <span style={{ color: '#FFCB47' }}>{formatValue(c.demandThreshold)}</span>）
                  {i < changes.length - 1 && '，'}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const mapPressureContent = (
    item: NonNullable<V2AirCompressorOverviewGetStrategyListPostResponse['supplyPressureGuaranteeStrategies']>[number]
  ) => {
    const suggestOpenText =
      item.suggestOpen
        ?.map(suggest => `${suggest.station || '--'} ${suggest.airCompressors?.join('、') || ''}`)
        .filter(Boolean)
        .join('、') ?? '';

    return (
      <div className={styles.accordionBody}>
        <div className={styles.pressureCondition}>
          {' '}
          不满足供气条件{formatValue(item.needPressureMin, 'Mpa')}~{formatValue(item.needPressureMax, 'Mpa')}，需要开启
          <span style={{ color: '#16DD8E' }}>{suggestOpenText || '--'}</span>
          ，保障供气压力。
        </div>
      </div>
    );
  };

  return (
    <Spin spinning={isLoading}>
      <div className={styles.strategyContainer}>
        {/* ---- 产线功率卡片 ---- */}
        {/* <div className={styles.circuitCards}>
          {circuitList.map((circuit, idx) => (
            <div key={circuit.circuitId ?? idx} className={styles.circuitCard}>
              <div className={styles.cardHeader}>
                <span className={styles.circuitName}>{fixedCircuitNames[idx]}</span>
                <DoubleRightOutlined
                  style={{ color: '#4a90e2', cursor: 'pointer' }}
                  onClick={() => handleDetailClick(circuit.circuitId)}
                />
              </div>
              <div className={styles.cardBody}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>实时功率(kW):</span>
                  <span className={styles.value}>{formatSummaryValue(circuit.power)}</span>
                </div>
                <div className={styles.dataRow}>
                  <span className={styles.label}>需量阈值(kW):</span>
                  <span className={styles.value}>{formatSummaryValue(circuit.threshold)}</span>
                </div>
              </div>
            </div>
          ))}
        </div> */}
        {/* ---- 空压机总计 ---- */}
        <div>
          <div className={styles.statusTotal}>
            <span className={styles.statusTotalLabel}>空压机总计：</span>
            <span className={styles.statusTotalValue}>{compressorTotal === undefined ? '--' : compressorTotal}台</span>
          </div>
          <div className={styles.statusStats}>
            {Object.entries(StatusMap).map(([key, { label, icon }]) => (
              <div key={key} className={styles.statusItem}>
                <span>
                  <img src={icon} alt={label} />
                  <span className={styles.statusText}>{label}</span>
                </span>
                <span className={styles.statusCount}>
                  {statusCount?.[key as keyof V2AirCompressorOverviewCountPostResponse] ?? '--'}台
                </span>
              </div>
            ))}
          </div>
        </div>
        {/* 策略 */}
        <div className={styles.strategyContent}>
          {/* ---- Tab 切换 ---- */}
          <div className={styles.tabBar}>
            {Object.entries(StrategyTabLabels).map(([key, label]) => (
              <div
                key={key}
                className={`${styles.tabBtn} ${activeTab === key ? styles.tabBtnActive : ''}`}
                onClick={() => setActiveTab(key as StrategyTab)}
              >
                {label}
              </div>
            ))}
          </div>

          {/* ---- 需量控制策略 ---- */}
          {activeTab === StrategyTab.demand && (
            <div className={styles.strategyPanel}>
              {demandStrategies.length === 0 ? (
                renderEmptyState(
                  '基于实时监测数据，系统判断当日无需启动需量优化策略，建议保持现有运行模式，持续关注功率变化趋势。'
                )
              ) : (
                <div className={styles.accordionList}>
                  {demandStrategies.map((item, idx) => {
                    return (
                      <Collapse
                        key={idx}
                        ghost={true}
                        expandIconPosition="right"
                        items={[
                          {
                            key: '1',
                            label: (
                              <div className={styles.accordionHeader}>
                                <div className={styles.headerLeft}>
                                  <div>
                                    <img src={strategyIcon} alt="策略" />
                                    <span className={styles.headerTitle}>{item.circuit ?? '--'}</span>
                                  </div>
                                  <div>
                                    <ClockCircleOutlined />
                                    <span className={styles.headerTime}>
                                      {item.time ? dayjs(item.time).format('YYYY年M月DD日 HH:mm:ss') : '--'}
                                    </span>
                                  </div>
                                </div>
                                <div className={styles.headerRight}>
                                  <span className={styles.powerInfo}>
                                    当前功率: <span>{formatValue(item.currentPower)}</span>
                                  </span>
                                  <span className={styles.thresholdInfo}>
                                    控制阈值: <span>{formatValue(item.demandThreshold)}</span>
                                  </span>
                                </div>
                              </div>
                            ),
                            children: mapPowerContent(item),
                          },
                        ]}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ---- 供气压力保障策略 ---- */}
          {activeTab === StrategyTab.pressure && (
            <div className={styles.strategyPanel}>
              {pressureStrategies.length === 0 ? (
                renderEmptyState(
                  '当日各监测点位压力持续正常，系统适合评估运行阀值与日元策略调整气供需，建议维持当前供气参数，确保持续稳定满足生产用气需求。'
                )
              ) : (
                <div className={styles.accordionList}>
                  {pressureStrategies.map((ps, idx) => {
                    return (
                      <Collapse
                        key={idx}
                        ghost={true}
                        expandIconPosition="right"
                        items={[
                          {
                            key: '1',
                            label: (
                              <div className={styles.accordionHeader}>
                                <div className={styles.headerLeft}>
                                  <div>
                                    <img src={strategyIcon} alt="策略" />
                                    <span className={styles.headerTitle}>{ps.station ?? '--'}</span>
                                  </div>
                                  <div>
                                    <span className={styles.headerTime} style={{ marginLeft: 20 }}>
                                      {dayjs().format('YYYY年M月DD日 HH:mm:ss')}
                                    </span>
                                  </div>
                                </div>
                                <div className={styles.headerRight}>
                                  <span className={styles.pressureInfo}>
                                    当前总管压力: <span>{formatValue(ps.pressure, 'Mpa')}</span>
                                  </span>
                                </div>
                              </div>
                            ),
                            children: mapPressureContent(ps),
                          },
                        ]}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default CompressorStrategy;
